import { useRef, type KeyboardEvent } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (content: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export default function ChatInput({
  value,
  onChange,
  onSend,
  disabled = false,
  placeholder = 'Type a message...',
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useMediaQuery('(max-width: 767px)');

  const handleSend = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // On desktop: send on Enter (without Shift)
    // On mobile: let Enter insert a newline; user must tap send button
    if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    // Auto-resize
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  };

  return (
    <div className="border-t border-neutral-200 bg-primary-10 p-4">
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className="flex-1 resize-none rounded-xl border border-neutral-300 px-4 py-2.5 text-sm
            placeholder:text-neutral-400 text-neutral-300 focus:border-primary-500 focus:outline-none focus:ring-2
            focus:ring-primary-500 focus:ring-offset-0 disabled:cursor-not-allowed disabled:bg-neutral-100"
          aria-label="Message input"
        />
        <button
          onClick={handleSend}
          disabled={!value.trim() || disabled}
          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary-600
            text-primary-10 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-neutral-300"
          aria-label="Send message"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
